import React from 'react';
import { Buildings, ChartBar, DoorOpen, Users, WarningCircle, type Icon } from '@phosphor-icons/react';
import { eur, initialen, qm } from '../../../../lib/immobilien/format';
import type { Portfolio } from '../../../../lib/immobilien/portfolio';
import type { TicketQuote } from '../../../../lib/immobilien/useObjektListe';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';

/** Portfolio-Kopf der Objektliste (Prototyp, Screens 01/02/50). */
export interface PortfolioWidgetsProps {
  kennzahlen: Portfolio;
  /** Soll-Kaltmiete der letzten 12 Monate (aufsteigend) */
  sollVerlauf: number[];
  monate: string[];
  tickets: TicketQuote | null;
  leerstand: { id: string; name: string; frei: number }[];
  zustaendig: { name: string; anzahl: number }[];
  /** aktuell gewählter Zuständigen-Filter */
  zustaendigFilter: string | null;
  onZustaendig: (name: string | null) => void;
  onObjekt: (id: string) => void;
}

const MONAT = new Intl.DateTimeFormat('de-DE', { month: 'short', year: '2-digit', timeZone: 'UTC' });
const monatKurz = (iso: string) => MONAT.format(new Date(`${iso}T00:00:00Z`)).replace('.', '');

function Kachel({ spalten = 1, zeilen = 1, akzent = false, children, style }: {
  spalten?: 1 | 2; zeilen?: 1 | 2; akzent?: boolean; children: React.ReactNode; style?: React.CSSProperties;
}) {
  return (
    <div
      className={`${akzent ? 'immo-akzent' : 'immo-glas'} relative flex flex-col overflow-hidden min-w-0`}
      style={{ gridColumn: `span ${spalten}`, gridRow: `span ${zeilen}`, borderRadius: 28, padding: '18px 20px', ...style }}
    >
      {children}
    </div>
  );
}

function KachelTitel({ icon: IconKomp, farbe, children, akzent = false }: { icon: Icon; farbe: string; children: React.ReactNode; akzent?: boolean }) {
  return (
    <span className="flex items-center" style={{ gap: 8, fontSize: 12, fontWeight: 600, letterSpacing: '0.01em', color: akzent ? 'var(--acc-ink)' : 'var(--ink2)' }}>
      <span className="flex items-center justify-center shrink-0"
        style={{ width: 24, height: 24, borderRadius: 8, background: akzent ? 'var(--acc-ink-fill)' : 'var(--fill2)', boxShadow: `inset 0 0 0 0.5px ${akzent ? 'var(--acc-ink-fill)' : 'var(--line)'}` }}>
        <IconKomp weight="fill" size={14} color={farbe} />
      </span>
      {children}
    </span>
  );
}

const RUND = "ui-rounded,'SF Pro Rounded',-apple-system,sans-serif";

/** Drei konzentrische Ringe (Belegung, Mieteingang, Tickets). Wert null = keine Daten (nur Spur). */
function Ringe({ ringe }: { ringe: { label: string; wert: number | null; farbe: string; spur: string }[] }) {
  const radien = [52, 38, 24];
  return (
    <svg width="118" height="118" viewBox="0 0 120 120" style={{ flexShrink: 0, transform: 'rotate(-90deg)' }} aria-hidden>
      {ringe.map((r, i) => {
        const radius = radien[i] ?? 24;
        const umfang = 2 * Math.PI * radius;
        const anteil = r.wert === null ? 0 : Math.max(0, Math.min(100, r.wert)) / 100;
        return (
          <g key={r.label}>
            <circle cx="60" cy="60" r={radius} fill="none" stroke={r.spur} strokeWidth="12" />
            {anteil > 0 && (
              <circle cx="60" cy="60" r={radius} fill="none" stroke={r.farbe} strokeWidth="12" strokeLinecap="round"
                strokeDasharray={`${umfang * anteil} ${umfang}`} />
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function PortfolioWidgets({
  kennzahlen, sollVerlauf, monate, tickets, leerstand, zustaendig, zustaendigFilter, onZustaendig, onObjekt,
}: PortfolioWidgetsProps) {
  const aktuelleSoll = sollVerlauf[sollVerlauf.length - 1] ?? 0;
  const maxSoll = Math.max(1, ...sollVerlauf);
  const ticketQuote = tickets && tickets.gesamt > 0 ? Math.round((tickets.erledigt / tickets.gesamt) * 100) : null;
  const ringe = [
    { label: 'Belegung', wert: kennzahlen.mietEinheiten > 0 ? kennzahlen.belegungProzent : null, farbe: 'var(--acc)', spur: 'var(--acc-soft)' },
    // Ohne Zahlungsdaten (Mietkonto, E4) gibt es noch keine Eingangsquote.
    { label: 'Mieteingang', wert: null, farbe: 'var(--ok)', spur: 'var(--ok-soft)' },
    { label: 'Tickets erledigt', wert: ticketQuote, farbe: 'var(--info)', spur: 'var(--info-soft)' },
  ];
  const ersterMonat = monate[0];
  const letzterMonat = monate[monate.length - 1];

  return (
    <section
      aria-label="Portfolio"
      className="grid grid-cols-2 lg:grid-cols-4"
      style={{ gridAutoRows: 170, gap: 14, gridAutoFlow: 'dense' }}
    >
      <Kachel spalten={2} zeilen={2} akzent style={{ justifyContent: 'space-between', padding: '20px 22px' }}>
        <div className="flex items-center justify-between" style={{ gap: 8 }}>
          <KachelTitel icon={Buildings} farbe="var(--acc-ink)" akzent>Portfolio</KachelTitel>
          <span className="immo-zahl" title="Soll-Kaltmiete × 12"
            style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 12, background: 'var(--acc-ink-fill)' }}>
            {eur((aktuelleSoll * 12) / 1000, { nachkomma: 0 }).replace(' €', ' T€')} / Jahr
          </span>
        </div>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span className="immo-zahl" style={{ fontFamily: RUND, fontSize: 72, fontWeight: 600, letterSpacing: '-0.05em', lineHeight: 0.95 }}>{kennzahlen.objekte}</span>
          <span style={{ fontSize: 15, fontWeight: 600 }}>
            Objekte · {kennzahlen.einheiten.toLocaleString('de-DE')} Einheiten · {qm(Math.round(kennzahlen.flaeche))}
          </span>
        </div>
        {/* Mieteingang der letzten 12 Monate braucht Zahlungsdaten – startet mit der Buchhaltung. */}
        <div className="flex flex-col" style={{ gap: 6 }}>
          <div style={{ height: 70, borderRadius: 12, border: '1.5px dashed var(--acc-ink-fill)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: 'var(--acc-ink2)' }}>
            {BUCHHALTUNG_FOLGT}
          </div>
          <div className="flex justify-between" style={{ fontSize: 11, fontWeight: 600, opacity: 0.7 }}>
            <span>{ersterMonat ? monatKurz(ersterMonat) : ''}</span>
            <span>Mieteingang, 12 Monate</span>
            <span>{letzterMonat ? monatKurz(letzterMonat) : ''}</span>
          </div>
        </div>
      </Kachel>

      <Kachel spalten={2} style={{ flexDirection: 'row', alignItems: 'center', gap: 18 }}>
        <Ringe ringe={ringe} />
        <div className="flex flex-col min-w-0" style={{ gap: 8 }}>
          {ringe.map(r => (
            <div key={r.label} className="flex flex-col">
              <span style={{ fontSize: 12, fontWeight: 600, color: r.farbe }}>{r.label}</span>
              <span className="immo-zahl" style={{ fontFamily: RUND, fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.1 }}
                title={r.wert === null ? (r.label === 'Mieteingang' ? BUCHHALTUNG_FOLGT : 'Keine Daten') : undefined}>
                {r.wert === null ? '–' : `${r.wert} %`}
              </span>
            </div>
          ))}
        </div>
      </Kachel>

      <Kachel style={{ justifyContent: 'space-between', padding: 18 }}>
        <KachelTitel icon={WarningCircle} farbe="var(--bad)">Mietrückstand</KachelTitel>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span className="immo-zahl" style={{ fontFamily: RUND, fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--ink3)' }}>–</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink3)' }}>{BUCHHALTUNG_FOLGT}</span>
        </div>
      </Kachel>

      <Kachel style={{ justifyContent: 'space-between', padding: 18 }}>
        <div className="flex flex-col">
          <KachelTitel icon={ChartBar} farbe="var(--acc)">Soll-Miete</KachelTitel>
          <span className="immo-zahl" title="Netto-Kaltmiete aller laufenden Mietverhältnisse im aktuellen Monat"
            style={{ fontFamily: RUND, fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {eur(aktuelleSoll, { nachkomma: 0 })}
          </span>
        </div>
        <div className="flex items-end" style={{ gap: 4, height: 46 }} aria-hidden>
          {sollVerlauf.map((v, i) => (
            <div key={monate[i] ?? i} title={`${monate[i] ? monatKurz(monate[i]) : ''}: ${eur(v, { nachkomma: 0 })}`}
              style={{ flex: 1, borderRadius: 3, height: `${Math.max(8, Math.round((v / maxSoll) * 100))}%`, background: i === sollVerlauf.length - 1 ? 'var(--acc)' : 'var(--fill3)' }} />
          ))}
        </div>
      </Kachel>

      <Kachel spalten={2} style={{ gap: 10 }}>
        <div className="flex items-center justify-between" style={{ gap: 8 }}>
          <KachelTitel icon={DoorOpen} farbe="var(--warn)">Leerstand</KachelTitel>
          <span style={{ fontSize: 12, color: 'var(--ink2)' }}>{kennzahlen.frei} {kennzahlen.frei === 1 ? 'Einheit' : 'Einheiten'} frei</span>
        </div>
        <div className="flex flex-col" style={{ gap: 6 }}>
          {leerstand.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Alle Einheiten vermietet.</span>}
          {leerstand.map(l => (
            <button key={l.id} type="button" onClick={() => onObjekt(l.id)}
              className="flex items-center cursor-pointer text-left" style={{ gap: 10, fontSize: 13, border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warn)', flexShrink: 0 }} />
              <span className="flex-1 min-w-0 truncate" style={{ fontWeight: 500 }}>{l.name}</span>
              <span style={{ color: 'var(--ink2)', whiteSpace: 'nowrap' }}>{l.frei} frei</span>
            </button>
          ))}
        </div>
      </Kachel>

      <Kachel spalten={2} style={{ gap: 10 }}>
        <KachelTitel icon={Users} farbe="var(--acc)">Zuständigkeiten</KachelTitel>
        <div className="flex justify-between" style={{ gap: 4 }}>
          {zustaendig.map((z, i) => {
            const an = zustaendigFilter === z.name;
            return (
              <button key={z.name} type="button" aria-pressed={an} title={`Nur Objekte von ${z.name}`}
                onClick={() => onZustaendig(an ? null : z.name)}
                className="flex flex-col items-center flex-1 min-w-0 cursor-pointer" style={{ gap: 5, border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)' }}>
                <span className="flex items-center justify-center"
                  style={{
                    width: 40, height: 40, borderRadius: '50%', fontSize: 13, fontWeight: 600,
                    background: an || (zustaendigFilter === null && i === 0) ? 'var(--acc-fill)' : 'var(--fill3)',
                    color: an || (zustaendigFilter === null && i === 0) ? 'var(--acc-ink)' : 'var(--ink)',
                    boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 1px 0 var(--line), inset 0 0 0 0.5px var(--line)',
                  }}>
                  {z.name === 'Global' ? 'GL' : initialen(z.name)}
                </span>
                <span className="truncate w-full text-center" style={{ fontSize: 11, color: 'var(--ink2)', lineHeight: 1.2 }}>{z.name.split(' ')[0]}</span>
                <span style={{ fontSize: 11, fontWeight: 700, marginTop: -4 }}>{z.anzahl}</span>
              </button>
            );
          })}
        </div>
      </Kachel>
    </section>
  );
}
